/*
 * <unik-search>: the header search (F5-01, F6-03), after the Shella reference
 * (assets/unik-search-popup.js in shella-dev-final). One real search field; focusing it
 * opens a panel below the field with "Avbryt", the popular searches and Colorblock's
 * predictive search results. The page is not scroll-locked, so nothing shifts.
 *
 * Closing: "Avbryt" and the X clear the field; Escape and a click on the backdrop below
 * the panel keep the text. Clicks elsewhere (for example the header around the field) do
 * not close the panel, and neither does the field losing focus: the results stay while the
 * panel is open, so "Done" on a phone keyboard leaves the products in view (S-01, the guard
 * in predictive-search.js onFocusOut). Loaded by snippets/unik-header-search.liquid.
 */
if (!customElements.get('unik-search')) {
  class UnikSearch extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.input = this.querySelector('.unik-search__input');
      this.field = this.querySelector('.unik-search__field');
      this.panel = this.querySelector('[data-unik-search-panel]');
      this.backdrop = this.querySelector('[data-unik-search-backdrop]');
      this.closeButton = this.querySelector('.unik-search__close');
      this.predictive = this.querySelector('predictive-search');
      if (!this.input || !this.field || !this.panel || !this.backdrop) return;

      this.controller = new AbortController();
      const { signal } = this.controller;
      const show = () => this.show();

      this.input.addEventListener('focus', show, { signal });
      this.input.addEventListener('click', show, { signal });
      this.input.addEventListener('input', show, { signal });

      this.querySelector('.unik-search__submit')?.addEventListener(
        'click',
        (event) => {
          if (this.input.value.trim()) return;
          event.preventDefault();
          this.input.focus();
          this.show();
        },
        { signal }
      );

      this.querySelectorAll('[data-unik-search-close]').forEach((button) => {
        button.addEventListener(
          'click',
          (event) => {
            // After a finger tap the focus does not return to the field, and the field lets
            // go of it (Colorblock's reset focuses it): on a phone the keyboard would come
            // back over the closed panel. A keyboard click (detail 0) returns it (tiltak 1.7 L6).
            const touch = event.detail > 0 && this.pointerType === 'touch';
            this.hide({ clear: true, restoreFocus: !touch });
            if (touch) this.input.blur();
          },
          { signal }
        );
      });
      this.backdrop.addEventListener('click', () => this.hide(), { signal });

      // "Søk etter «…»" is a button in the panel, outside the form (Colorblock has it
      // inside), so neither a click nor Enter on it (Colorblock clicks the selected option)
      // would submit the search.
      this.panel.addEventListener(
        'click',
        (event) => {
          if (!event.target.closest('.predictive-search__item--term')) return;
          event.preventDefault();
          this.input.form?.requestSubmit();
        },
        { signal }
      );

      this.addEventListener(
        'keydown',
        (event) => {
          if (event.key !== 'Escape' || !this.isOpen) return;
          event.preventDefault();
          event.stopPropagation();
          this.hide();
        },
        { signal }
      );

      // While open, a click on something that cannot take focus (the header around the
      // field, the panel's background, the backdrop) keeps the focus in the field, so
      // Colorblock's predictive search does not close its results. Otherwise pressing on
      // the backdrop collapses the results before the click closes the panel. Links in the
      // panel also keep it until the click: Safari can otherwise blur the field before the
      // link activates. Only in the header: the panel and the backdrop are inside it, and
      // the rest of the page keeps text selection and other scripts' presses (tiltak 3.2).
      (this.closest('.header-wrapper') || this).addEventListener(
        'pointerdown',
        (event) => {
          this.pointerType = event.pointerType;
          if (!this.isOpen || event.button !== 0) return;
          const link = event.target.closest('a[href]');
          const interactive = event.target.closest('a[href], button, input, select, textarea, summary, label, [tabindex]');
          if ((link && this.panel.contains(link)) || !interactive) event.preventDefault();
        },
        { signal, capture: true }
      );

      const reposition = () => {
        if (this.isOpen) this.position();
      };
      window.addEventListener('resize', reposition, { signal });
      window.addEventListener('scroll', reposition, { signal, passive: true });

      document.fonts?.ready.then(() => this.fit());
    }

    /**
     * Desktop: the closed, empty field is as wide as its placeholder, and unik-header.css
     * widens it while open. Measured once the font has loaded, so another placeholder text
     * still fits; the CSS fallback is the width of today's text. The width transition is
     * switched on only after this, so the page does not animate on load.
     */
    fit() {
      const context = (UnikSearch.canvas ||= document.createElement('canvas')).getContext('2d');
      if (context && !this.isOpen && this.field.getClientRects().length) {
        const style = getComputedStyle(this.input);
        context.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
        // The space after the text mirrors the search icon's space before it.
        const start = this.input.getBoundingClientRect().left - this.field.getBoundingClientRect().left;
        const width = Math.ceil(2 * start + context.measureText(this.input.placeholder).width);
        this.style.setProperty('--unik-search-fit', `${width}px`);
      }
      getComputedStyle(this).width;
      this.classList.add('is-sized');
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
    }

    get isOpen() {
      return !this.panel.hidden;
    }

    show() {
      if (this.returningFocus) return;
      if (!this.isOpen) {
        this.panel.hidden = false;
        this.backdrop.hidden = false;
        if (this.closeButton) this.closeButton.hidden = false;
        this.setAttribute('open', '');
        // The field usually still has focus after closing, so no focus event reopens
        // Colorblock's results: show the cached results for the same term here.
        const predictive = this.predictive;
        if (
          predictive?.getAttribute('results') === 'true' &&
          predictive.searchTerm &&
          predictive.searchTerm === this.input.value.trim()
        ) {
          predictive.open?.();
        }
      }
      this.position();
    }

    /**
     * @param {object} [options]
     * @param {boolean} [options.clear=false] - Empty the field and the results ("Avbryt", X).
     * @param {boolean} [options.restoreFocus=true] - Return focus to the field without reopening.
     */
    hide({ clear = false, restoreFocus = true } = {}) {
      if (!this.isOpen) return;
      // Focus returns first: Colorblock's focus handler reopens its results, and the
      // close below must come after it, or the results stay "open" behind the closed
      // panel (aria-expanded, and the sticky header stops reacting to scrolling).
      if (restoreFocus) {
        this.returningFocus = true;
        this.input.focus({ preventScroll: true });
        this.returningFocus = false;
      }
      if (clear) this.clear();
      // Also after a reset: Colorblock's reset closes the results but leaves isOpen set, and
      // the sticky header would stop reacting to scrolling.
      this.predictive?.close?.();
      this.panel.hidden = true;
      this.backdrop.hidden = true;
      if (this.closeButton) this.closeButton.hidden = true;
      this.removeAttribute('open');
    }

    /**
     * Empties the field through the form's reset event, which Colorblock's search form
     * handles: the value, the search term, the pending request and the results (tiltak 3.1).
     */
    clear() {
      const form = this.input.form;
      if (!form) {
        this.input.value = '';
        return;
      }
      // Colorblock skips the reset while a result is chosen with the arrow keys
      // (shouldResetForm); the X and "Avbryt" always clear.
      this.predictive?.querySelectorAll('[aria-selected="true"]').forEach((option) => option.setAttribute('aria-selected', 'false'));
      // Its reset handler focuses the field, which must not reopen the panel.
      this.returningFocus = true;
      form.reset();
      this.returningFocus = false;
    }

    position() {
      const rect = this.field.getBoundingClientRect();
      if (rect.bottom <= 0 || !this.field.getClientRects().length) {
        this.hide({ restoreFocus: false });
        return;
      }
      // 8 px below the field, but never below the top of the header's bottom border: below
      // 750 px the header ends 7 px under the field, and its border and a 1 px strip of the
      // page would show above the panel.
      let top = rect.bottom + 8;
      const header = this.closest('.header-wrapper');
      if (header) {
        const border = parseFloat(getComputedStyle(header).borderBottomWidth) || 0;
        top = Math.min(top, header.getBoundingClientRect().bottom - border);
      }
      this.style.setProperty('--unik-search-top', `${Math.floor(top)}px`);
      this.style.setProperty('--unik-search-inline', `${Math.round(rect.left)}px`);
    }
  }

  customElements.define('unik-search', UnikSearch);
}
